import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import {
  useCreateTicket,
  useSupportTickets,
} from '@/hooks/useSupportChat';
import { TicketList } from './TicketList';
import { TicketConversation } from './TicketConversation';

/** Bouton flottant + panneau de tchat support pour shipper/transporter/driver. */
export const SupportChatFAB = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const { data: tickets = [] } = useSupportTickets();
  const createTicket = useCreateTicket();

  const role = user?.role;
  const visible = role === 'shipper' || role === 'transporter' || role === 'driver';

  const unreadCount = useMemo(
    () => tickets.filter((t) => t.unread_for_user).length,
    [tickets]
  );

  const activeTicket = tickets.find((t) => t.id === activeId) || null;

  const handleNew = async () => {
    const tk = await createTicket.mutateAsync(undefined);
    setActiveId(tk.id);
  };

  if (!visible) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-40 w-14 h-14 rounded-full bg-[#FFBF07] hover:bg-[#E5AB06] active:bg-[#CC9805] text-[#2D3748] shadow-lg transition-all duration-200 flex items-center justify-center"
        aria-label={t('supportChat.open')}
      >
        <MessageCircle className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#FF7171] text-white text-[11px] font-semibold flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md p-0 flex flex-col gap-0"
        >
          <SheetHeader className="px-4 py-3 border-b border-[#DBE2EA]">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-[#37699E] font-['Lexend_Deca']">
                {activeTicket ? t('supportChat.title') : t('supportChat.yourTickets')}
              </SheetTitle>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-hidden">
            {activeTicket ? (
              <TicketConversation
                ticket={activeTicket}
                onBack={() => setActiveId(null)}
                asAdmin={false}
              />
            ) : (
              <TicketList
                tickets={tickets}
                activeId={activeId}
                onSelect={setActiveId}
                onNew={handleNew}
                unreadSide="user"
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
